import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { useTRPC } from "@/trpc";
import { Building2, MoreHorizontal, Pencil } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Organization {
  id: string;
  name: string;
  created_at: string;
  business_units?: Array<{
    id: string;
    name: string;
    created_at: string;
  }>;
}

const OrganizationsPage = () => {
  const nameInputId = useId();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const { data: organizations = [], isLoading } = useQuery(
    trpc.organization.getOrganizations.queryOptions(),
  );

  const createOrgMutation = useMutation(
    trpc.organization.createOrganization.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getOrganizations.queryOptions(),
        );
        handleCloseDialog();
      },
    }),
  );

  const updateOrgMutation = useMutation(
    trpc.organization.updateOrganization.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.organization.getOrganizations.queryOptions(),
        );
        handleCloseDialog();
      },
    }),
  );

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setOrgName("");
    setEditingOrg(null);
  };

  const handleOpenDialog = (org?: Organization) => {
    if (org) {
      setEditingOrg(org);
      setOrgName(org.name);
    } else {
      setOrgName("");
      setEditingOrg(null);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingOrg) {
      updateOrgMutation.mutate({ id: editingOrg.id, name: orgName });
    } else {
      createOrgMutation.mutate({ name: orgName });
    }
  };

  const isPending = createOrgMutation.isPending || updateOrgMutation.isPending;

  return (
    <div className="min-h-full min-w-0 bg-slate-50">
      <div className="mx-auto max-w-7xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-4 sm:py-8 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
            Organizations
          </h1>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="w-full shrink-0 sm:w-auto"
                onClick={() => handleOpenDialog()}
              >
                Add Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="!w-[calc(100vw-2rem)] !max-w-lg !max-h-[calc(100dvh-2rem)] gap-5 overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 text-slate-900 sm:rounded-2xl sm:p-6">
              <DialogHeader className="pr-6 text-left">
                <DialogTitle className="text-xl leading-7">
                  {editingOrg ? "Edit Organization" : "Add New Organization"}
                </DialogTitle>
                <DialogDescription className="text-sm leading-6 text-slate-600">
                  {editingOrg
                    ? "Update this organization's name."
                    : "Create an organization to group its business units."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid min-w-0 gap-2">
                  <Label htmlFor={nameInputId}>Organization Name</Label>
                  <Input
                    id={nameInputId}
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Enter organization name"
                  />
                </div>
                <DialogFooter className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end sm:space-x-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseDialog}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending
                      ? editingOrg
                        ? "Saving..."
                        : "Creating..."
                      : editingOrg
                        ? "Save Changes"
                        : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="min-w-0 overflow-hidden rounded-2xl border-slate-200 bg-white p-0 shadow-xs">
          <div
            className="max-w-full overflow-x-auto focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            role="region"
            aria-label="Organizations table"
            tabIndex={0}
          >
            <Table className="min-w-[540px] text-sm">
              <TableHeader className="bg-slate-50 [&_th]:px-4 [&_th]:text-slate-600">
                <TableRow className="border-slate-200 hover:bg-slate-50">
                  <TableHead className="min-w-48">Name</TableHead>
                  <TableHead className="whitespace-nowrap">
                    Created At
                  </TableHead>
                  <TableHead className="sticky right-0 z-20 bg-slate-50 text-right shadow-[-1px_0_0_0_var(--color-slate-200)]">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="[&_td]:px-4 [&_td]:py-4 [&_td]:text-slate-700">
                {isLoading ? (
                  // Loading skeleton rows
                  Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={`loading-${index}`}>
                      <TableCell>
                        <Skeleton className="h-4 w-32 rounded-full"></Skeleton>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24 rounded-full"></Skeleton>
                      </TableCell>
                      <TableCell className="sticky right-0 z-10 bg-white shadow-[-1px_0_0_0_var(--color-slate-200)]">
                        <div className="flex justify-end gap-2">
                          <Skeleton className="h-12 w-12 rounded-xl sm:w-20"></Skeleton>
                          <Skeleton className="hidden h-12 w-44 rounded-xl sm:block"></Skeleton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : organizations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="py-10 text-center text-slate-500"
                    >
                      No organizations found. Create your first organization to
                      get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  organizations.map((org) => (
                    <TableRow
                      key={org.id}
                      className="group border-slate-200 hover:bg-slate-50/70"
                    >
                      <TableCell className="max-w-sm font-medium text-slate-900 [overflow-wrap:anywhere]">
                        {org.name}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {new Date(org.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="sticky right-0 z-10 bg-white text-right shadow-[-1px_0_0_0_var(--color-slate-200)] group-hover:bg-slate-50">
                        <div className="hidden items-center justify-end gap-2 sm:flex">
                          <Button
                            variant="outline"
                            onClick={() => handleOpenDialog(org)}
                          >
                            Edit
                          </Button>
                          <Button variant="outline" asChild>
                            <Link
                              to="/admin/organizations/$orgId/business-units"
                              params={{ orgId: org.id }}
                            >
                              View Business Units
                            </Link>
                          </Button>
                        </div>
                        <div className="sm:hidden">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-12 px-0"
                                aria-label={`Actions for ${org.name}`}
                              >
                                <MoreHorizontal
                                  aria-hidden="true"
                                  className="size-5 text-slate-500"
                                />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="max-w-[calc(100vw-2rem)] rounded-xl border-slate-200 bg-white p-1.5 text-slate-700 shadow-lg"
                            >
                              <DropdownMenuItem
                                className="min-h-12 cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary"
                                onClick={() => handleOpenDialog(org)}
                              >
                                <Pencil
                                  aria-hidden="true"
                                  className="mr-2 size-4 shrink-0"
                                />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                asChild
                                className="min-h-12 cursor-pointer rounded-lg focus:bg-primary/5 focus:text-primary"
                              >
                                <Link
                                  to="/admin/organizations/$orgId/business-units"
                                  params={{ orgId: org.id }}
                                >
                                  <Building2
                                    aria-hidden="true"
                                    className="mr-2 size-4 shrink-0"
                                  />
                                  View Business Units
                                </Link>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_admin/admin/organizations/")({
  component: OrganizationsPage,
});
